import { useState, useEffect, useCallback } from 'react';
import { caretakerSearchService, Caretaker, Specialty, SearchFilters } from '../services/caretakerSearchService';

interface UseCaretakerSearchReturn {
  // Data
  caretakers: Caretaker[];
  specialties: Specialty[];
  loading: boolean;
  error: string | null;
  
  // Search state
  filters: SearchFilters;
  currentPage: number;
  totalPages: number;
  totalResults: number;
  
  // Actions
  setFilters: (filters: SearchFilters) => void;
  updateFilter: (key: keyof SearchFilters, value: any) => void;
  clearFilters: () => void;
  search: (page?: number, append?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  
  // Suggestions
  getCaretakerSuggestions: (query: string) => Promise<Caretaker[]>;
  getSpecialtySuggestions: (query: string) => Promise<string[]>;
}

const initialFilters: SearchFilters = {
  query: '',
  specialty: '',
  availability: undefined,
  minRating: undefined,
  services: [],
};

export const useCaretakerSearch = (): UseCaretakerSearchReturn => {
  const [caretakers, setCaretakers] = useState<Caretaker[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<SearchFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);

  // Load specialties on mount
  useEffect(() => {
    const loadSpecialties = async () => {
      try {
        const response = await caretakerSearchService.getSpecialties();
        setSpecialties(response.data);
      } catch (err) {
        console.error('Error loading specialties:', err);
      }
    };

    loadSpecialties();
  }, []);

  // Search function
  const search = useCallback(async (page: number = 1, append: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await caretakerSearchService.searchCaretakers(filters, page, 12);
      
      if (append) {
        setCaretakers(prev => [...prev, ...response.data]);
      } else {
        setCaretakers(response.data);
      }
      
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalResults(response.pagination.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while searching';
      setError(errorMessage);
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Update filters and trigger search
  const setFilters = useCallback((newFilters: SearchFilters) => {
    setFiltersState(newFilters);
    setCurrentPage(1);
    // Search will be triggered by useEffect
  }, []);

  // Update a single filter
  const updateFilter = useCallback((key: keyof SearchFilters, value: any) => {
    setFiltersState(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
    // Search will be triggered by useEffect
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFiltersState(initialFilters);
    setCurrentPage(1);
    // Search will be triggered by useEffect
  }, []);

  // Load more results
  const loadMore = useCallback(async () => {
    if (currentPage < totalPages && !loading) {
      await search(currentPage + 1, true);
    }
  }, [currentPage, totalPages, loading, search]);

  // Get caretaker suggestions
  const getCaretakerSuggestions = useCallback(async (query: string): Promise<Caretaker[]> => {
    return await caretakerSearchService.getCaretakerSuggestions(query);
  }, []);

  // Get specialty suggestions
  const getSpecialtySuggestions = useCallback(async (query: string): Promise<string[]> => {
    return await caretakerSearchService.getSpecialtySuggestions(query);
  }, []);

  // Trigger search when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Create a new search function with current filters to avoid dependency issues
      const performSearch = async () => {
        setLoading(true);
        setError(null);

        try {
          const response = await caretakerSearchService.searchCaretakers(filters, 1, 12);
          setCaretakers(response.data);
          setCurrentPage(response.pagination.page);
          setTotalPages(response.pagination.totalPages);
          setTotalResults(response.pagination.total);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An error occurred while searching';
          setError(errorMessage);
          console.error('Search error:', err);
        } finally {
          setLoading(false);
        }
      };

      performSearch();
    }, 500); // Increased debounce to 500ms for better UX

    return () => clearTimeout(timeoutId);
  }, [filters]);

  return {
    // Data
    caretakers,
    specialties,
    loading,
    error,
    
    // Search state
    filters,
    currentPage,
    totalPages,
    totalResults,
    
    // Actions
    setFilters,
    updateFilter,
    clearFilters,
    search,
    loadMore,
    
    // Suggestions
    getCaretakerSuggestions,
    getSpecialtySuggestions,
  };
};
